import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import CalendarIcon from '@3plearning/3p-teacheradmin-assets/assets/icons/icon_calendar.svg';
import { Calendar, DropdownMenu } from '@3plearning/react-components';
import {
  CUSTOM_DATE,
  DATE_OPTIONS,
  DEFAULT_DATE_OPTIONS,
  LAST_WEEK,
  THIS_SCHOOL_YEAR,
  THIS_WEEK,
  LIFETIME,
  CUSTOM_DATE_TIME,
  DEFAULT_DATE_OPTIONS_LABELS
} from './date-picker-constants';
import DateHelper from '../../helpers/date-helper';
import DatePickerWrapper from './styled';

/**
 * DatePicker component
 * Allows user to pick 'This week' 'Last week' 'This school year' or 'Custom date' from drop down
 * Loads Calendar component if user chooses custom date range
 * Calls onDateChange callback prop when selection changes
 */
class DatePicker extends Component {
  static propTypes = {
    /** Range to limit the date selection to */
    academicYear: PropTypes.shape({
      startDate: PropTypes.string,
      endDate: PropTypes.string
    }).isRequired,

    /** Current selected date */
    currentDate: PropTypes.shape({
      type: PropTypes.oneOf(DATE_OPTIONS),
      startDate: PropTypes.string,
      endDate: PropTypes.string
    }).isRequired,

    /** To show the Spanish or any passed language in the dropdown */
    labels: PropTypes.objectOf(PropTypes.oneOf(DATE_OPTIONS)),

    /** Options to display in dropdown menu */
    options: PropTypes.arrayOf(PropTypes.oneOf(DATE_OPTIONS)),

    /** Callback when a date selection is made */
    onDateChange: PropTypes.func.isRequired,

    initialDate: PropTypes.shape({
      startDate: PropTypes.string,
      endDate: PropTypes.string
    }),

    /** Flag whether custom date range should be mon-sun
     *  enabled by default
     */
    weeklyRangeOnly: PropTypes.bool,

    /** Callback when a time selection is made
     */
    onTimeChange: PropTypes.func,

    /** Custom class to attach to parent div - use this to override any styles */
    className: PropTypes.string,

    /** Props overrides for react-dates component
     * @see <a href="https://github.com/airbnb/react-dates#make-some-awesome-datepickers">react-dates props</a>
     */
    reactDatesConfig: PropTypes.object
  };

  static defaultProps = {
    labels: DEFAULT_DATE_OPTIONS_LABELS,
    options: DEFAULT_DATE_OPTIONS
  };

  constructor(props) {
    super(props);
    this.state = {
      weeklyRangeOnly:
        props.weeklyRangeOnly !== undefined ? props.weeklyRangeOnly : true,
      selectedDateType: props.currentDate.type,
      selectedCustomDates:
        props.currentDate.type === CUSTOM_DATE ||
        props.currentDate.type === CUSTOM_DATE_TIME,
      hideSelectedTime: true,
      time: ['9:00AM', '5:00PM'],
      dateTimeSubmitted: false
    };
  }

  /* eslint camelcase: "off" */
  UNSAFE_componentWillReceiveProps(nextProps) {
    if (this.state.selectedDateType !== nextProps.currentDate.type) {
      this.setState({
        weeklyRangeOnly:
          nextProps.weeklyRangeOnly !== undefined
            ? nextProps.weeklyRangeOnly
            : true,
        selectedDateType: nextProps.currentDate.type,
        selectedCustomDates: nextProps.currentDate.type === CUSTOM_DATE
      });
    }
  }

  /**
   * Runs when user changes value in dropdown
   * Calls onDateChange prop with newly selected date range (unless user chose custom date)
   */
  onDateChange = (value) => {
    this.setState({
      selectedDateType: value,
      selectedCustomDates: false,
      selectedTimeRange: false,
      dateTimeSubmitted: false
    });

    let startDate;
    let endDate;

    switch (value) {
      case THIS_WEEK:
        ({ startDate, endDate } = DateHelper.getThisWeekStartEnd());
        break;
      case LAST_WEEK:
        ({ startDate, endDate } = DateHelper.getLastWeekStartEnd());
        break;
      case THIS_SCHOOL_YEAR:
      case LIFETIME:
        ({ startDate, endDate } = this.props.academicYear);
        break;
      case CUSTOM_DATE:
      case CUSTOM_DATE_TIME:
        return;
      default:
        console.warn('Unhandled date type'); // eslint-disable-line no-console
    }

    this.props.onDateChange(value, startDate, endDate);
  };

  /**
   * Runs when user selects start & end dates in calendar component
   * Calls onDateChange prop with newly selected date range
   */
  onCustomDateChange = (startDate, endDate) => {
    this.props.onDateChange(this.state.selectedDateType, startDate, endDate);
    this.setState({ selectedCustomDates: true });
  };

  onTimeRangeChange = (time) => {
    this.props.onTimeChange(time);
    this.setState({ selectedTimeRange: true, time });
  };

  /**
   * Reverts date to previous selection if user cancelled selecting custom dates in calendar
   */
  onCustomDateClose = () => {
    // todo: i don't like this timeout but i couldn't get cancel functionality to
    // work without it
    setTimeout(() => {
      if (!this.state.selectedCustomDates) {
        this.setState({
          selectedDateType: this.props.currentDate.type,
          selectedCustomDates: true,
          hideSelectedTime: true
        });
      }
    });
  };

  onDateTimeSubmit = () => {
    this.setState({
      selectedDateType: CUSTOM_DATE_TIME,
      selectedCustomDates: true,
      hideSelectedTime: true,
      dateTimeSubmitted: true
    });
  };
  /**
   * Generates the display text to show in the dropdown menu as the current selected option
   * Handles showing the date range if user chose custom date ie. 14 June 2018 - 28 June 2018
   */
  getSelectedLabel = () => {
    const { selectedDateType } = this.state;

    if (
      selectedDateType === CUSTOM_DATE ||
      selectedDateType === CUSTOM_DATE_TIME
    ) {
      const { time } = this.state;
      const { startDate, endDate } = this.props.currentDate;
      const formatString = 'DD MMM YYYY';
      const formattedStartDate = moment(startDate).format(formatString);
      const formattedEndDate = moment(endDate).format(formatString);
      return selectedDateType === CUSTOM_DATE
        ? `${formattedStartDate} - ${formattedEndDate}`
        : `${formattedStartDate} ${time[0]} - ${formattedEndDate} ${time[1]}`;
    }

    return selectedDateType;
  };

  render() {
    const {
      selectedDateType,
      selectedCustomDates,
      hideSelectedTime,
      dateTimeSubmitted,
      weeklyRangeOnly
    } = this.state;
    const {
      academicYear,
      reactDatesConfig,
      className,
      options,
      initialDate,
      labels
    } = this.props;
    const selectedDateLabel = this.getSelectedLabel();
    const shouldDisplayCalendar =
      (selectedDateType === CUSTOM_DATE && !selectedCustomDates) ||
      (selectedDateType === CUSTOM_DATE_TIME &&
        !dateTimeSubmitted &&
        (!selectedCustomDates || hideSelectedTime));
    return (
      <DatePickerWrapper className={className}>
        {shouldDisplayCalendar && (
          <Calendar
            range={academicYear}
            reactDatesConfig={reactDatesConfig}
            onDatesPicked={this.onCustomDateChange}
            weeklyRangeOnly={weeklyRangeOnly}
            initialDate={initialDate}
            includeTimeRange={selectedDateType === CUSTOM_DATE_TIME}
            onTimesPicked={(time) => this.onTimeRangeChange(time)}
            onClose={this.onCustomDateClose}
            onSubmit={this.onDateTimeSubmit}
          />
        )}
        {!shouldDisplayCalendar && (
          <DropdownMenu
            icon={CalendarIcon}
            options={options.map((option) => ({
              label: labels[option] ? labels[option] : option,
              value: option
            }))}
            selectedOption={{
              label: labels[selectedDateLabel]
                ? labels[selectedDateLabel]
                : selectedDateLabel,
              value: selectedDateType
            }}
            onSelectOption={(option) => this.onDateChange(option.value)}
          />
        )}
      </DatePickerWrapper>
    );
  }
}

DatePicker.THIS_WEEK = THIS_WEEK;
DatePicker.LAST_WEEK = LAST_WEEK;
DatePicker.THIS_SCHOOL_YEAR = THIS_SCHOOL_YEAR;
DatePicker.CUSTOM_DATE = CUSTOM_DATE;
DatePicker.CUSTOM_DATE_TIME = CUSTOM_DATE_TIME;
DatePicker.LIFETIME = LIFETIME;
DatePicker.DEFAULT_DATE_OPTIONS = DEFAULT_DATE_OPTIONS;
DatePicker.DATE_OPTIONS = DATE_OPTIONS;

export default DatePicker;



// WEBPACK FOOTER //
// ./components/date-picker/index.js